































































































import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { checkMobile } from '../../utils/utils'
import ChartLine from '../atoms_molecules/ChartLine.vue'
import UserTrackingData from '../../../api/v2/models/user_tracking_data'
import api from '../../../api/v2/api'
import DateRangePicker from 'vue2-daterange-picker'
import customRange, { localeData } from '../../../../helpers/date-range'
import { subMonths, format } from 'date-fns'
import { ROUTER_NAME } from '../../router'
import { convertMinutesToTimeJp } from '../../../../lib/convert'

// 折れ線グラフで使用するラベル名
const LEARN_TIME_TEXT = '学習時間'

@Component({
  components: {
    ChartLine,
    DateRangePicker
  }
})
export default class DashboardChart extends Vue {
  @Prop({ default: '' }) bannerHtml!: string

  // 折れ線グラフで使うデータ
  data: {
    labels: any[]
    datasets: any[]
  } = {
    labels: [],
    datasets: []
  }

  // 日付範囲（初期値は過去1ヶ月〜今日）
  dateRange = {
    startDate: subMonths(new Date(), 1),
    endDate: new Date()
  }

  // DateRangePicker用
  localeData = localeData
  customRange = customRange()

  // トラッキング（学習時間や平均など）用データ
  userTrackingData: UserTrackingData | null = null

  // /api/v2/activities から取得するアクティビティの配列
  activitiesData: any[] = []

  // レスポンシブ判定
  isMobile: boolean = checkMobile()

  // コンポーネント生成時に読み込み
  created() {
    this.updateDateRange()
  }

  // Chart.js のオプション設定
  chartOptions = {
    elements: {
      line: {
        tension: 0
      }
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem, data) => {
          const label = data.datasets[tooltipItem.datasetIndex].label || ''
          const value = Math.round(tooltipItem.yLabel * 100) / 100
          if (label === LEARN_TIME_TEXT) {
            return `${label}: ${convertMinutesToTimeJp(value)}`
          } else {
            return `${label}: ${value}`
          }
        }
      }
    },
    scales: {
      x: {
        display: true
      },
      y: {
        display: true,
        ticks: {
          stepSize: 60
        }
      }
    }
  }

  /**
   * 日付範囲が変わるたびにAPIを呼び出す
   * - 1つ目: /api/v2/tracking (学習時間や平均値など => グラフ用)
   * - 2つ目: /api/v2/activities (リスト表示用 => テーブル用)
   */
  async updateDateRange() {
    // 親へ現在のdateRangeを通知（不要なら削除OK）
    this.$emit('rangeDate', this.dateRange)

    // 1. トラッキングデータ取得（グラフに使用）
    this.userTrackingData = await api.tracking.get(this.dateRange)

    // 2. アクティビティデータ取得（テーブルに使用）
    const response = await api.activity.get(0, this.dateRange)
    this.activitiesData = response.activities || []

    // グラフ描画用のデータをセット
    this.data = {
      labels: this.userTrackingData.labels,
      datasets: [
        {
          label: LEARN_TIME_TEXT,
          borderColor: 'rgb(67, 67, 72)',
          data: this.userTrackingData.trackingUserValues,
          fill: false,
          pointBorderWidth: 2
        }
      ]
    }
  }

  // テーブルのカラム定義
  headers = [
    {
      text: '学習日',
      align: 'start',
      sortable: false,
      value: 'label'
    },
    { text: '学習時間', value: 'tracking' },
    { text: '学習完了数', value: 'activity' }
  ]

  /**
   * テーブルに表示するデータ
   * - userTrackingData.labels にある日付ごとに行を作る
   * - 同じ日付に含まれるアクティビティを filter で全部集め、activitiesForDate.length を表示
   * - 分数(trackingMinutes)も同時に保持し、後で平均を計算しやすくする
   */
  get tableData() {
    if (!this.userTrackingData) {
      return []
    }

    return this.userTrackingData.labels
      .map((label, index) => {
        // ラベル(例: '2025-02-16')を 'yyyy-MM-dd' に変換
        const dateStr = format(new Date(label), 'yyyy-MM-dd')

        // 該当の日付にマッチするアクティビティを全部集める
        const activitiesForDate = this.activitiesData.filter((act: any) => {
          const actDateStr = format(new Date(act.createdAt), 'yyyy-MM-dd')
          return actDateStr === dateStr
        })

        // その日の学習完了数
        const activityCount = activitiesForDate.length

        // 学習時間(分)を取り出す（仮に trackingUserValues が分単位で入っている想定）
        const minutes = this.userTrackingData.trackingUserValues[index]

        return {
          // 表示用の日付
          label: label,
          // 「X時間Y分」の文字列
          tracking: convertMinutesToTimeJp(minutes),
          // 計算用に minutes も保持
          trackingMinutes: minutes,
          // 学習完了数
          activity: activityCount
        }
      })
      // 必要に応じて日付の並びを逆転
      .reverse()
  }

  /**
   * ▼▼▼ ここから追加した「フロントエンドで平均を算出する」ための computed ▼▼▼
   */
  get averageTrackingTime(): string {
    // テーブルデータがなければ 0時間0分
    if (!this.tableData.length) return '0時間0分'

    // 合計を出す
    const totalMinutes = this.tableData.reduce((acc, row) => {
      return acc + row.trackingMinutes
    }, 0)

    // 日数で割る
    const avgMinutes = totalMinutes / this.tableData.length

    // 分を「X時間Y分」に変換
    return convertMinutesToTimeJp(Math.round(avgMinutes))
  }

  get averageActivityCount(): string {
    if (!this.tableData.length) return '0'

    // 合計
    const totalCount = this.tableData.reduce((acc, row) => {
      return acc + row.activity
    }, 0)

    // 平均(小数点1桁)
    const avg = totalCount / this.tableData.length
    return avg.toFixed(1)
  }
  /**
   * ▲▲▲ ここまで追加した computed ▲▲▲
   */
}
